import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs';

import { ClinicalAssessment } from '@sondermind/utilities/models-assessments-monolith';
import { AssessmentProgressHttpService, UpdateEvent } from '../services/assessment-progress.httpservice';
import { IAssessmentMessagePairing } from '../models/assessment-message-pairing.model';
import { IAssessmentAssignment } from '../models/assessment-assignment.interface';

@Injectable({
  providedIn: 'root'
})
export class AssessmentProgressStore {
  /**
   * Tracks the remaining assessments that a user has to complete
   */
  private assessmentsToDo: IAssessmentMessagePairing[] = [];

  /**
   * Tracks the current assessment in progress (should be equal to assessmentsToDo[0])
   */
  private currentAssessmentInProgressSubject: BehaviorSubject<IAssessmentMessagePairing | null>
    = new BehaviorSubject<IAssessmentMessagePairing | null>(null);

  currentAssessment$: Observable<IAssessmentMessagePairing | null>
    = this.currentAssessmentInProgressSubject.asObservable();

  /** Tracking context if a provider started the assessment(s) */
  startedByProviderData: {
    providerId: number; // current user
    personaClientId: number; // client who's assessments are being completed
    assessmentLaunchedFrom: 'video' | 'provider_portal'; // where the assessment modal was launched from
    dateAdministered: string; // "date administered" entered by the provider. E.g. '2025-04-16'
  } | undefined;

  // Emitted to when we want to finish completing CQs
  assessmentsCompleted$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private assessmentProgressHttpService: AssessmentProgressHttpService,
  ) {}

  setPatchUrlRoute(route: string): void {
    this.assessmentProgressHttpService.setPatchUrlRoute(route);
  }

  pushAssessmentsToDo(assessments: ClinicalAssessment[]): void {
    this.assessmentsToDo = this.assessmentsToDo.concat(assessments.map((a) => (
      { appMessageDeliveryId: a.appMessageDeliveryId, assessmentType: a.assessmentType }
    )));
  }

  pushMicroserviceAssessmentsToDo(assessments: IAssessmentAssignment[]): void {
    this.assessmentsToDo = this.assessmentsToDo.concat(assessments.map((a) => (
      { assignmentId: a.id, assessmentType: a.assessmentType }
    )));
  }

  clearAssessmentsToDo(): void {
    this.assessmentsToDo = [];
  }

  getFirstAssessment(): IAssessmentMessagePairing {
    return this.assessmentsToDo[0];
  }

  startNextAssessmentToDo$(): Observable<IAssessmentMessagePairing> {
    const nextAssessment = this.assessmentsToDo[0];
    this.currentAssessmentInProgressSubject.next(nextAssessment);

    return this.assessmentProgressHttpService.patchAssessmentProgressState(
      nextAssessment?.appMessageDeliveryId, UpdateEvent.OPENED
    ).pipe(
      map(() => nextAssessment)
    );
  }

  // If no assessment found with that message id, just start the next available one
  startNextAssessmentByMessageId$(appMessageDeliveryId: string): Observable<IAssessmentMessagePairing> {
    const nextAssessment = this.assessmentsToDo.find(
      (assessment) => assessment.appMessageDeliveryId.toString() === appMessageDeliveryId
    );

    if (nextAssessment) {
      this.currentAssessmentInProgressSubject.next(nextAssessment);

      return this.assessmentProgressHttpService.patchAssessmentProgressState(
        nextAssessment?.appMessageDeliveryId, UpdateEvent.OPENED
      ).pipe(
        map(() => nextAssessment)
      );
    }

    return this.startNextAssessmentToDo$();
  }

  markCurrentAssessmentAsCompleted$(): Observable<void> {
    const completedAssessment = this.currentAssessmentInProgressSubject.value;
    if (completedAssessment) {
      return this.assessmentProgressHttpService.patchAssessmentProgressState(
        completedAssessment.appMessageDeliveryId, UpdateEvent.COMPLETED
      ).pipe(
        tap(() => {
          const remainingAssessments = this.assessmentsToDo.filter(
            (assessment) => assessment.appMessageDeliveryId !== completedAssessment.appMessageDeliveryId
          );

          this.assessmentsToDo = remainingAssessments;
        })
      );
    }

    return of(null);
  }

  // This does not mark assessments complete in the BE,
  // but instead allows the current progress of completing any assessments
  // to be finished and to exit the flow
  finishCurrentAssessmentProgress(): void {
    this.clearAssessmentsToDo();
    this.assessmentsCompleted$.next(true);
  }

  getRemainingAssessments(): IAssessmentMessagePairing[] {
    return this.assessmentsToDo;
  }

  hasRemainingAssessments(): boolean {
    return this.getRemainingAssessments().length > 0;
  }
}
